<template>
  <div class="permission-container">
    <el-card class="box-card">
      <div slot="header" class="card-header">
        <span>权限设置</span>
        <el-select
          v-model="currentRoleId"
          placeholder="请选择角色"
          size="medium"
          style="width: 220px"
          @change="handleRoleChange"
        >
          <el-option
            v-for="role in roleList"
            :key="role.id"
            :label="role.roleName"
            :value="role.id"
          />
        </el-select>
      </div>

      <!-- 菜单树 + 按钮权限 -->
      <div v-loading="loading">
        <el-tree
          ref="menuTree"
          :data="menuTreeData"
          show-checkbox
          node-key="id"
          :default-checked-keys="checkedMenuIds"
          :props="treeProps"
          :check-strictly="true"
          default-expand-all
          @check="handleCheck"
        >
          <template slot-scope="{ node, data }">
            <span class="tree-node">
              <i v-if="data.menuType === 1" class="el-icon-folder-opened menu-icon dir-icon"></i>
              <i v-else-if="data.menuType === 2" class="el-icon-document menu-icon page-icon"></i>
              <i v-else-if="data.menuType === 3" class="el-icon-thumb menu-icon btn-icon"></i>
              <span>{{ data.menuName }}</span>
              <el-tag
                v-if="data.menuType === 3 && data.perms"
                size="mini"
                type="warning"
                effect="dark"
              >按钮: {{ data.perms }}</el-tag>
              <el-tag
                v-else
                :type="tagTypeMap[data.menuType]"
                size="mini"
              >{{ typeLabelMap[data.menuType] }}</el-tag>
            </span>
          </template>
        </el-tree>

        <div class="action-bar">
          <el-button type="primary" icon="el-icon-check" :loading="saving" @click="handleSave">
            保存权限配置
          </el-button>
        </div>
      </div>
    </el-card>

    <!-- 权限说明 -->
    <el-card class="box-card mt-16">
      <div slot="header"><span>权限说明</span></div>
      <div class="help-text">
        <p><strong>目录</strong>：菜单分组，如「系统设置」</p>
        <p><strong>页面菜单</strong>：具体功能页面，如「会员管理」「订单管理」</p>
        <p><strong>按钮权限</strong>：页面内的操作按钮，如「导出」「查询余额」等</p>
        <el-divider></el-divider>
        <h4>预置角色说明：</h4>
        <ul>
          <li><strong>超级管理员</strong> - 拥有所有权限（不可修改）</li>
          <li><strong>查看员</strong> - 仅可查看会员管理、订单管理、有赞余额流水三个页面</li>
          <li><strong>操作员</strong> - 除查看权限外，还拥有所有导出按钮和查询余额按钮权限</li>
        </ul>
      </div>
    </el-card>
  </div>
</template>

<script>
import { getRoleList, assignMenus } from '@/api/role'
import { getMenuTree, getRoleMenus } from '@/api/menu'

export default {
  name: 'PermissionSetting',
  data() {
    return {
      loading: false,
      saving: false,
      roleList: [],
      currentRoleId: null,
      menuTreeData: [],
      checkedMenuIds: [],
      treeProps: {
        children: 'children',
        label: 'menuName'
      },
      tagTypeMap: { 1: '', 2: 'success', 3: 'warning' },
      typeLabelMap: { 1: '目录', 2: '页面', 3: '按钮' }
    }
  },
  created() {
    this.loadRoles()
    this.loadMenuTree()
  },
  methods: {
    async loadRoles() {
      try {
        const res = await getRoleList()
        this.roleList = res.data || []
        if (this.roleList.length > 0) {
          // 默认选中第一个非 SUPER_ADMIN 角色
          const targetRole = this.roleList.find(r => r.roleCode !== 'SUPER_ADMIN') || this.roleList[0]
          this.currentRoleId = targetRole.id
          await this.loadCheckedMenus(this.currentRoleId)
        }
      } catch (e) {
        console.error('加载角色列表失败', e)
      }
    },
    async loadMenuTree() {
      try {
        const res = await getMenuTree()
        this.menuTreeData = res.data || []
      } catch (e) {
        console.error('加载菜单树失败', e)
      }
    },
    async loadCheckedMenus(roleId) {
      if (!roleId) return
      this.loading = true
      try {
        const res = await getRoleMenus(roleId)
        this.checkedMenuIds = res.data || []
        // 等待 DOM 更新后刷新树选中状态
        this.$nextTick(() => {
          if (this.$refs.menuTree) {
            this.$refs.menuTree.setCheckedKeys(this.checkedMenuIds)
          }
        })
      } catch (e) {
        console.error('加载角色权限失败', e)
      } finally {
        this.loading = false
      }
    },
    handleRoleChange(roleId) {
      this.loadCheckedMenus(roleId)
    },

    handleCheck(data, info) {
      const checkedNodeIds = info.checkedKeys
      const checkedNodeKeys = info.checkedNodes
      const halfCheckedKeys = info.halfCheckedKeys

      // 只有 type=1 目录需要级联：勾选目录时级联所有后代
      if (data.menuType === 1) {
        const allDescendantIds = this.collectDescendantIds(data)
        if (checkedNodeIds.includes(data.id)) {
          // 勾选目录 → 连带勾选所有后代
          const merged = [...new Set([...checkedNodeIds, ...allDescendantIds])]
          this.$refs.menuTree.setCheckedKeys(merged)
        } else {
          // 取消目录 → 连带取消所有后代
          const toRemove = new Set([data.id, ...allDescendantIds])
          const remaining = checkedNodeIds.filter(id => !toRemove.has(id))
          this.$refs.menuTree.setCheckedKeys(remaining)
        }
      }

      // 同步到内部状态（取勾选 + 半选的）
      this.checkedMenuIds = [...checkedNodeIds, ...halfCheckedKeys]
    },

    // 递归收集节点的所有后代 ID
    collectDescendantIds(node) {
      const ids = []
      const walk = (n) => {
        if (n.children && n.children.length) {
          n.children.forEach(child => {
            ids.push(child.id)
            walk(child)
          })
        }
      }
      walk(node)
      return ids
    },
    async handleSave() {
      if (!this.currentRoleId) {
        this.$message.warning('请先选择角色')
        return
      }

      this.saving = true
      try {
        await assignMenus(this.currentRoleId, this.checkedMenuIds)
        this.$message.success('权限配置保存成功')
      } catch (e) {
        this.$message.error('保存失败: ' + (e.message || ''))
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style lang="less" scoped>
.permission-container {
  max-width: 900px;
}

.mt-16 {
  margin-top: 16px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.tree-node {
  display: flex;
  align-items: center;
  gap: 8px;

  .menu-icon {
    font-size: 14px;
  }
  .dir-icon { color: #E6A23C; }
  .page-icon { color: #409EFF; }
  .btn-icon { color: #67C23A; }
}

.action-bar {
  margin-top: 20px;
  text-align: center;
}

.help-text {
  line-height: 1.8;
  color: #606266;

  h4 { margin-bottom: 8px; }
  ul { padding-left: 20px; }
  li { margin-bottom: 4px; }
}
</style>
